import { BrandLoader } from "@/components/brand/BrandLoader";
import { EmptyState } from "@/components/brand/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { propertyService } from "@/lib/api";
import { uploadService } from "@/lib/api/upload";
import { useAuthStore } from "@/stores/auth";
import { usePropertyStore } from "@/stores/property";
import type { OtaConnection, OtaName, Property, PropertyInput } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Copy, Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const CHANNEX_BADGE: Record<Property["channex_status"], { label: string; className: string }> = {
  none: { label: "미연동", className: "bg-muted text-muted-foreground" },
  pending_approval: { label: "승인 대기", className: "bg-yellow-100 text-yellow-800" },
  pending: { label: "연결 대기", className: "bg-amber-100 text-amber-700" },
  approved: { label: "승인 완료", className: "bg-sky-100 text-sky-700" },
  integrating: { label: "연동 진행중", className: "bg-indigo-100 text-indigo-700" },
  connected: { label: "연동 완료", className: "bg-primary/10 text-primary" },
};

const OTA_META: { key: OtaName; label: string; hotelIdLabel: string }[] = [
  { key: "agoda", label: "Agoda", hotelIdLabel: "YCS Hotel ID" },
  { key: "booking", label: "Booking.com", hotelIdLabel: "Booking.com Property ID" },
  { key: "tripcom", label: "Trip.com", hotelIdLabel: "Trip.com Hotel ID" },
];

const emptyOta = (ota: OtaName): OtaConnection => ({
  ota,
  hotel_id: "",
  account_email: "",
  note: "",
});

const EMPTY: PropertyInput = {
  name: "",
  address: "",
  phone: "",
  wifi_ssid: "",
  wifi_password: "",
  check_in_time: "",
  check_out_time: "",
  reminders_enabled: false,
  house_rules: "",
  extra_info: "",
  intro: "",
  intro_image_urls: [],
  ota_connections: [],
  support_email: "",
  support_phone: "",
  cms_requested: false,
};

// ─── Form dialog (create + edit share the same fields) ───────────────────────

interface PropertyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits an existing property; otherwise it creates one. */
  property?: Property | null;
  isPending?: boolean;
  onSubmit: (values: PropertyInput) => void;
}

function PropertyFormDialog({
  open,
  onOpenChange,
  property,
  isPending,
  onSubmit,
}: PropertyFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PropertyInput>({ defaultValues: EMPTY });

  const loginEmail = useAuthStore((s) => s.user?.email ?? "");

  useEffect(() => {
    if (open) {
      reset(
        property
          ? { ...EMPTY, ...property, cms_requested: property.channex_status !== "none" }
          : { ...EMPTY, support_email: loginEmail }
      );
    }
  }, [open, property, reset, loginEmail]);

  const urls = watch("intro_image_urls") ?? [];
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    setUploading(true);
    try {
      const next = [...urls];
      for (const f of files) {
        if (next.length >= 5) break;
        const { url } = await uploadService.upload(f);
        next.push(url);
        setValue("intro_image_urls", next, { shouldDirty: true });
      }
    } catch {
      toast.error("이미지 업로드에 실패했습니다.");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i: number) =>
    setValue(
      "intro_image_urls",
      urls.filter((_, idx) => idx !== i),
      { shouldDirty: true }
    );

  const cmsRequested = watch("cms_requested");
  // pending 이상(승인 후)이면 해제 불가 — 서버 409와 일치.
  const cmsLocked = Boolean(
    property && !["none", "pending_approval"].includes(property.channex_status)
  );

  const otaConns = watch("ota_connections") ?? [];
  const otaAt = (key: OtaName) => otaConns.find((c) => c.ota === key);
  const toggleOta = (key: OtaName, on: boolean) =>
    setValue(
      "ota_connections",
      on ? [...otaConns, emptyOta(key)] : otaConns.filter((c) => c.ota !== key),
      { shouldDirty: true }
    );
  const setOtaField = (key: OtaName, field: "hotel_id" | "account_email" | "note", v: string) =>
    setValue(
      "ota_connections",
      otaConns.map((c) => (c.ota === key ? { ...c, [field]: v } : c)),
      { shouldDirty: true }
    );

  const submit = handleSubmit((values) => {
    if ((values.ota_connections ?? []).some((c) => !c.hotel_id.trim())) {
      toast.error("선택한 OTA의 호텔 ID를 입력하세요.");
      return;
    }
    onSubmit(values);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{property ? "숙소 수정" : "새 숙소 등록"}</DialogTitle>
          <DialogDescription>
            컨시어지가 안내에 활용하는 숙소 프로필입니다. 등록한 숙소는 운영팀 확인을 거쳐 채널
            매니저에 연결됩니다.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <section className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="prop-name">숙소명 *</Label>
              <Input id="prop-name" {...register("name", { required: "숙소명을 입력하세요" })} />
              {errors.name ? (
                <p className="text-xs text-destructive">{errors.name.message}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prop-phone">연락처</Label>
              <Input id="prop-phone" {...register("phone")} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="prop-address">주소</Label>
              <Input id="prop-address" {...register("address")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prop-check-in">체크인 시간</Label>
              <Input id="prop-check-in" {...register("check_in_time")} placeholder="15:00" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prop-check-out">체크아웃 시간</Label>
              <Input id="prop-check-out" {...register("check_out_time")} placeholder="11:00" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prop-wifi-ssid">Wi-Fi 이름</Label>
              <Input id="prop-wifi-ssid" {...register("wifi_ssid")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prop-wifi-pw">Wi-Fi 비밀번호</Label>
              <Input id="prop-wifi-pw" {...register("wifi_password")} />
            </div>
          </section>

          <section className="space-y-3">
            <div>
              <Label>지원팀 연락</Label>
              <p className="text-xs text-muted-foreground">
                연동·운영 문의 시 지원팀이 연락할 담당자 정보입니다.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="prop-support-email">지원팀 연락 이메일 *</Label>
                <Input
                  id="prop-support-email"
                  type="email"
                  {...register("support_email", { required: "이메일을 입력하세요" })}
                />
                {errors.support_email ? (
                  <p className="text-xs text-destructive">{errors.support_email.message}</p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="prop-support-phone">지원팀 연락 전화 (선택)</Label>
                <Input id="prop-support-phone" {...register("support_phone")} />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="prop-intro">소개 문구</Label>
              <Textarea
                id="prop-intro"
                {...register("intro")}
                className="min-h-[100px]"
                placeholder="게스트가 언어를 선택한 뒤 보게 되는 환영 소개입니다. (선택한 언어로 자동 번역됩니다)"
              />
            </div>
            <div className="space-y-1.5">
              <Label>소개 이미지 (최대 5장)</Label>
              <div className="flex flex-wrap gap-2">
                {urls.map((u, i) => (
                  <div key={u} className="relative h-24 w-24">
                    <img src={u} alt="" className="h-24 w-24 rounded object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAt(i)}
                      className="absolute -right-2 -top-2 rounded-full bg-black/70 px-1.5 text-xs text-white"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onPick}
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading || urls.length >= 5}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? "업로드 중…" : "이미지 추가"}
              </Button>
              <p className="text-xs text-muted-foreground">{urls.length}/5</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prop-rules">하우스 룰</Label>
              <Textarea id="prop-rules" {...register("house_rules")} className="min-h-[80px]" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prop-extra">기타 안내</Label>
              <Textarea id="prop-extra" {...register("extra_info")} className="min-h-[80px]" />
            </div>
          </section>

          <section className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                aria-label="체크인·체크아웃 안내 메일을 자동으로 보냅니다"
                checked={watch("reminders_enabled") ?? false}
                onChange={(e) =>
                  setValue("reminders_enabled", e.target.checked, { shouldDirty: true })
                }
              />
              체크인·체크아웃 안내 메일을 자동으로 보냅니다
            </label>
            <p className="text-xs text-muted-foreground">
              켜면 체크인 전날과 체크아웃 당일 아침에 게스트에게 안내 메일이 나갑니다. 메일에는
              게시한 안내만 실리므로, 켜기 전에 안내 콘텐츠를 게시해 주세요.
            </p>
          </section>

          <section className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                aria-label="채널 매니저(CMS) 연동을 신청합니다"
                checked={cmsRequested}
                disabled={cmsLocked}
                onChange={(e) => setValue("cms_requested", e.target.checked, { shouldDirty: true })}
              />
              채널 매니저(CMS) 연동을 신청합니다
            </label>
            <p className="text-xs text-muted-foreground">
              신청하면 운영팀 승인 후 OTA 채널에 연결됩니다.
              {cmsLocked ? " 이미 연동이 진행되어 해제할 수 없습니다." : ""}
            </p>
            {cmsRequested ? (
              <>
                <div>
                  <Label>OTA 연동</Label>
                  <p className="text-xs text-muted-foreground">
                    연동할 OTA를 선택하고 정보를 입력하면 운영팀이 승인 후 채널 매핑에 사용합니다.
                  </p>
                </div>
                {OTA_META.map(({ key, label, hotelIdLabel }) => {
                  const conn = otaAt(key);
                  return (
                    <div key={key} className="rounded-md border border-border/60 p-3">
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          aria-label={label}
                          checked={Boolean(conn)}
                          onChange={(e) => toggleOta(key, e.target.checked)}
                        />
                        {label}
                      </label>
                      {conn ? (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label htmlFor={`ota-${key}-hotel`}>{hotelIdLabel} *</Label>
                            <Input
                              id={`ota-${key}-hotel`}
                              value={conn.hotel_id}
                              onChange={(e) => setOtaField(key, "hotel_id", e.target.value)}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`ota-${key}-email`}>계정 이메일</Label>
                            <Input
                              id={`ota-${key}-email`}
                              value={conn.account_email}
                              onChange={(e) => setOtaField(key, "account_email", e.target.value)}
                            />
                          </div>
                          <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor={`ota-${key}-note`}>메모</Label>
                            <Input
                              id={`ota-${key}-note`}
                              value={conn.note}
                              onChange={(e) => setOtaField(key, "note", e.target.value)}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground">다른 OTA는 추후 확장 예정입니다.</p>
              </>
            ) : null}
          </section>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={isPending || uploading}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {property ? "저장" : "등록"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PropertiesPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Property | null>(null);
  const selectedPropertyId = usePropertyStore((s) => s.selectedPropertyId);
  const setSelectedPropertyId = usePropertyStore((s) => s.setSelectedPropertyId);

  const { data: properties, isLoading } = useQuery({
    queryKey: ["properties"],
    queryFn: propertyService.list,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["properties"] });

  const createMutation = useMutation({
    mutationFn: (body: PropertyInput) => propertyService.create(body),
    onSuccess: (created) => {
      invalidate();
      setDialogOpen(false);
      setSelectedPropertyId(created.id);
      if (created.channex_status === "none") {
        toast.success("숙소가 등록되었습니다.");
      } else if (created.channex_status === "pending_approval") {
        toast.info("숙소가 등록되었습니다. 운영팀 승인 후 채널이 연결됩니다.");
      } else if (created.channex_status === "pending") {
        toast.info("숙소가 등록되었습니다. 채널 연결은 대기 중입니다 — 연결 재시도를 눌러주세요.");
      } else {
        toast.success("숙소가 등록되고 채널 매니저에 연결되었습니다.");
      }
    },
    onError: () => toast.error("등록에 실패했습니다."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: PropertyInput }) =>
      propertyService.update(id, body),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditing(null);
      toast.success("저장되었습니다.");
    },
    onError: () => toast.error("저장에 실패했습니다."),
  });

  const connectMutation = useMutation({
    mutationFn: (id: string) => propertyService.connect(id),
    onSuccess: (property) => {
      invalidate();
      if (property.channex_status === "approved") {
        toast.success("채널 매니저 연결이 완료되었습니다.");
      } else {
        toast.error("연결에 실패했습니다. 잠시 후 다시 시도하세요.");
      }
    },
    onError: () => toast.error("연결에 실패했습니다. 잠시 후 다시 시도하세요."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => propertyService.remove(id),
    onSuccess: (_, id) => {
      invalidate();
      if (selectedPropertyId === id) setSelectedPropertyId(null);
      toast.success("삭제되었습니다.");
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        const detail = error.response.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail
            : "객실 타입·예약 등 하위 데이터가 있어 삭제할 수 없습니다."
        );
      } else {
        toast.error("삭제에 실패했습니다.");
      }
    },
  });

  const copyChatbotLinkMutation = useMutation({
    mutationFn: (id: string) => propertyService.chatbotLink(id),
    onSuccess: async ({ link_url }) => {
      try {
        await navigator.clipboard.writeText(link_url);
        toast.success("숙소 챗봇 링크가 복사되었습니다.", { description: link_url });
      } catch {
        toast.error("복사에 실패했습니다.");
      }
    },
    onError: () => toast.error("챗봇 링크를 가져오지 못했습니다."),
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (property: Property) => {
    setEditing(property);
    setDialogOpen(true);
  };

  const handleSubmit = (body: PropertyInput) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, body });
    } else {
      createMutation.mutate(body);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">숙소 관리</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            숙소를 등록·수정하고 채널 매니저 연결 상태를 확인합니다.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> 새 숙소
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <BrandLoader className="py-12" size={44} label="불러오는 중…" />
        ) : properties && properties.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>숙소명</TableHead>
                <TableHead>주소</TableHead>
                <TableHead>상태</TableHead>
                <TableHead className="text-right">관리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {properties.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{p.address || "-"}</TableCell>
                  <TableCell>
                    {(() => {
                      const badge = CHANNEX_BADGE[p.channex_status] ?? {
                        label: p.channex_status,
                        className: "bg-muted text-muted-foreground",
                      };
                      return (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={copyChatbotLinkMutation.isPending}
                        onClick={() => copyChatbotLinkMutation.mutate(p.id)}
                      >
                        <Copy className="h-4 w-4" /> 챗봇 링크 복사
                      </Button>
                      {p.channex_status === "pending" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={connectMutation.isPending}
                          onClick={() => connectMutation.mutate(p.id)}
                        >
                          <RefreshCw className="h-4 w-4" /> 연결 재시도
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="수정"
                        onClick={() => openEdit(p)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="삭제"
                        onClick={() => {
                          if (!window.confirm(`${p.name} 숙소를 삭제할까요?`)) return;
                          deleteMutation.mutate(p.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            title="아직 등록된 숙소가 없어요"
            description="숙소를 등록하면 객실 타입, 예약, 가이드를 숙소별로 관리할 수 있어요."
            action={
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" /> 새 숙소
              </Button>
            }
          />
        )}
      </Card>

      <PropertyFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        property={editing}
        isPending={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
